/* Phase 5 · HR-Lite — Leave requests (all app users can request + approve where they’re approver) */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LEAVE_TYPES = ["Casual", "Sick", "Other"];

export default function LeaveRequests() {
  const [mine, setMine] = useState([]);
  const [toMe, setToMe] = useState([]);
  const [form, setForm] = useState({ type: "Casual", from_date: "", to_date: "", note: "" });
  const [decisionNote, setDecisionNote] = useState({});

  const load = async () => {
    const [m, t] = await Promise.all([
      api.get("/hr/leave/requests", { params: { scope: "mine" } }),
      api.get("/hr/leave/requests", { params: { scope: "to_me" } }).catch(() => ({ data: [] })),
    ]);
    setMine(m.data);
    setToMe(t.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.from_date || !form.to_date) return toast.error("Dates required");
    try {
      const r = await api.post("/hr/leave/requests", form);
      toast.success(r.data.is_blackout ? "Requested (month-end blackout → Director)" : "Request submitted");
      setForm({ type: "Casual", from_date: "", to_date: "", note: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const decide = async (id, status) => {
    try {
      await api.post(`/hr/leave/requests/${id}/decide`, { status, decision_note: decisionNote[id] || "" });
      toast.success(status);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl" data-testid="hr-leaves">
      <h1 className="text-2xl md:text-3xl font-bold text-[#0B2340] mb-4">Leave Requests</h1>

      <div className="bg-white p-4 rounded-lg border border-slate-100 mb-6">
        <h3 className="text-sm font-semibold text-[#0B2340] mb-3">New request</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select className="border rounded px-2 py-1.5 text-sm" data-testid="lr-type"
            value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            {LEAVE_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <input type="date" className="border rounded px-2 py-1.5 text-sm" data-testid="lr-from"
            value={form.from_date} onChange={e => setForm({ ...form, from_date: e.target.value })} />
          <input type="date" className="border rounded px-2 py-1.5 text-sm" data-testid="lr-to"
            value={form.to_date} onChange={e => setForm({ ...form, to_date: e.target.value })} />
          <input placeholder="Note" className="border rounded px-2 py-1.5 text-sm"
            value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
        </div>
        <button onClick={submit} data-testid="lr-submit"
          className="mt-3 px-4 py-2 rounded-full bg-[#B08B49] text-white text-sm">Submit</button>
        <p className="text-[10px] text-[#7C8E9E] mt-2">
          Requests touching the last 3 working days of a month route to Director (month-end blackout).
        </p>
      </div>

      {toMe.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-[#0B2340] mb-2">Pending approvals ({toMe.filter(l => l.status === "Pending").length})</h3>
          <div className="bg-white rounded-lg border border-slate-100">
            {toMe.filter(l => l.status === "Pending").map(l => (
              <div key={l.id} className="p-3 border-b border-slate-50" data-testid={`lr-approve-${l.id}`}>
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                  <div className="text-sm">
                    <span className="font-medium">{l.type}</span> · {l.from_date} → {l.to_date} ({l.days}d)
                    {l.is_blackout && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800">BLACKOUT</span>}
                  </div>
                </div>
                {l.note && <div className="text-xs text-[#7C8E9E] italic">“{l.note}”</div>}
                <div className="flex gap-2 mt-2">
                  <input placeholder="Decision note" className="flex-1 border rounded px-2 py-1 text-xs"
                    value={decisionNote[l.id] || ""} onChange={e => setDecisionNote({ ...decisionNote, [l.id]: e.target.value })} />
                  <button onClick={() => decide(l.id, "Approved")} data-testid={`lr-approve-btn-${l.id}`}
                    className="px-3 py-1 rounded-full bg-emerald-600 text-white text-xs">Approve</button>
                  <button onClick={() => decide(l.id, "Rejected")} data-testid={`lr-reject-btn-${l.id}`}
                    className="px-3 py-1 rounded-full bg-red-500 text-white text-xs">Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-[#0B2340] mb-2">My requests</h3>
        <div className="bg-white rounded-lg border border-slate-100 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-[11px] uppercase text-[#7C8E9E]">
              <tr className="border-b border-slate-100">
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">From</th>
                <th className="text-left p-3">To</th>
                <th className="text-right p-3">Days</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Note</th>
              </tr>
            </thead>
            <tbody>
              {mine.map(l => (
                <tr key={l.id} className="border-b border-slate-50">
                  <td className="p-3">{l.type}</td>
                  <td className="p-3 text-xs">{l.from_date}</td>
                  <td className="p-3 text-xs">{l.to_date}</td>
                  <td className="p-3 text-right">{l.days}</td>
                  <td className="p-3 text-xs">{l.status} {l.is_blackout ? "· BO" : ""}</td>
                  <td className="p-3 text-xs">{l.decision_note || l.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
